import React from 'react'
import {BrowserRouter as Router} from 'react-router-dom'
import {createRoot} from 'react-dom/client'
import { AuthtoolProvider, init } from '@mia-platform-internal/authtool-client-js/react'

import App from './App'
import './index.css'
import {loadHubConfig} from './config'
import ThemeProvider from './ThemeProvider'
import AntdThemeBridge from './AntdThemeBridge'
import {applyThemeClasses} from './utils/themeMode'
import {EVENTS, track} from './analytics/analytics'
import {watchPlanRefusals} from './utils/planRefusal'
import {watchPreviewFrames, watchSessionExpiry, watchSessionOnFocus} from './utils/sessionExpiry'
import {watchStaleBuild} from './utils/staleBuild'
import {gateAiFoundry} from './utils/aiFoundryGate'
import {setRequestGate} from './utils/fetchRegistry'

// Apply dark mode on initial load if saved in localStorage
const applyInitialDarkMode = () => {
  const savedTheme = localStorage.getItem('theme') || 'light';
  // Default matches antd's dark-theme primary color (--ant-color-primary),
  // so a first-time user's chrome matches mia-chatbot-widget's look and feel
  // out of the box. A user who has explicitly picked their own accent color
  // (Theme settings) keeps it — this only changes what a NEW user starts with.
  const savedAccent = localStorage.getItem('accentColor') || '#1668DC';

  // Theme-class logic lives in utils/themeMode.js — shared with the
  // account-menu Sun/Moon toggle so both apply the exact same mapping.
  applyThemeClasses(savedTheme);

  // Apply saved accent color and font size
  document.documentElement.style.setProperty('--accent-color', savedAccent);
  document.documentElement.style.setProperty('--accent-primary', savedAccent);
  document.documentElement.style.setProperty('--palette-action-primary-default', savedAccent);
  document.documentElement.style.setProperty('--palette-action-link-default', savedAccent);
  document.documentElement.style.setProperty('--mia-purple-primary', savedAccent);
};

// Apply dark mode immediately (before React renders)
applyInitialDarkMode();

// Suppress harmless ResizeObserver loop errors and React defaultProps warnings
const resizeObserverLoopErrRe = /ResizeObserver loop/;
const defaultPropsWarningRe = /Support for defaultProps will be removed from function components/;
const originalError = window.console.error;
window.console.error = (...args) => {
  const firstArg = args[0];
  if (firstArg) {
    // Check if it's a string or Error object with ResizeObserver message
    const message = typeof firstArg === 'string' ? firstArg : firstArg.message || '';
    if (resizeObserverLoopErrRe.test(message) || defaultPropsWarningRe.test(message)) {
      return;
    }
  }
  originalError.apply(window.console, args);
};

// The vendored @mia-platform-internal/chatbot-widget (session feedback
// star/nudge UI) calls ADK's `.../sessions/:id/feedback` endpoint directly
// via the global fetch, with no prop to opt out (checked its ChatWidgetProps/
// ChatWidgetConfig types — there is none). Short-circuit those specific
// requests here instead of patching the vendored, minified dist bundle
// (which would be lost on the next reinstall from its tarball). GET returns
// an empty "no feedback recorded" body; PUT/DELETE just no-op with 204 —
// either way the widget never touches the network for this feature.
// Any base: `/api/adk/apps/...` (the BFF) or the vfs agent's own `/apps/...` (config.vfsAgentUrl), which 404'd.
const FEEDBACK_ENDPOINT_RE = /\/apps\/[^/]+\/users\/[^/]+\/sessions\/[^/]+\/feedback(?:[/?]|$)/;
// A chat turn the person's plan refuses (403 `plan_feature` from mia-flow-bff) is also announced here, so the chat
// panel can explain it: the widget's own transport gives Flow no hook for the response (see utils/planRefusal.js).
// With the person's AI Foundry connection off, no Catalog AI request leaves the browser (utils/aiFoundryGate.js), from
// the global fetch or the authorized one.
// A 401 from Flow's own APIs checks once whether the session expired; if so the person is told and sent to sign in
// (utils/sessionExpiry.js, components/SessionExpiredNotice.jsx).
// The widget may be handed the authorized fetch instead of window.fetch, so the feedback short-circuit sits in both.
const answerFeedback = (fetchImpl) => (input, init) => {
  const url = typeof input === 'string' ? input : (input?.url || '');
  if (FEEDBACK_ENDPOINT_RE.test(url)) {
    const method = (init?.method || (typeof input === 'object' && input?.method) || 'GET').toUpperCase();
    if (method === 'GET') {
      return Promise.resolve(new Response(JSON.stringify(null), {status: 200, headers: {'Content-Type': 'application/json'}}));
    }
    // The star rating the user gave the conversation is the one piece of "how did it feel" Flow has: report the number, only
    // (the widget's only other field is a free-text comment, which is never sent, so there is no `reason`).
    if (method === 'PUT') {
      try {
        const body = typeof init?.body === 'string' ? JSON.parse(init.body) : null;
        const rating = Number(body?.rating ?? body?.score ?? body?.value);
        if (Number.isFinite(rating)) { track(EVENTS.FEEDBACK, {rating}); }
      } catch (_error) { /* not a rating we can read */ }
    }
    return Promise.resolve(new Response(null, {status: 204}));
  }
  return fetchImpl(input, init);
};
const originalFetch = gateAiFoundry(watchSessionExpiry(watchPlanRefusals(window.fetch.bind(window))));
// The authorized fetch (utils/fetchRegistry.js) does not go through window.fetch: the same session watch there.
setRequestGate((fetchImpl) => answerFeedback(gateAiFoundry(watchSessionExpiry(fetchImpl))));
// The tab coming back after a while checks the session; Flow's preview frame saying "sign in" means the same
// (utils/sessionExpiry.js): a page that makes no API call still learns the session expired.
watchSessionOnFocus();
watchPreviewFrames();
// After a deploy, a tab on the previous build reloads once when a code file it asks for is gone (utils/staleBuild.js).
watchStaleBuild();
window.fetch = answerFeedback(originalFetch);

const rootComponent = () => {
  const basename = import.meta.env.BASE_URL
  const authtoolBaseUrl = '/bff'
  
  return (
    <AuthtoolProvider value={init({baseUrl: authtoolBaseUrl})}>
      <Router 
        basename={basename}
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true
        }}
      >
        <ThemeProvider>
          <AntdThemeBridge>
            <App />
          </AntdThemeBridge>
        </ThemeProvider>
      </Router>
    </AuthtoolProvider>
  )
}

let root

function retrieveContainer (props) {
  const {container} = props
  return container ? container.querySelector('#root') : document.querySelector('#root')
}

function render (props) {
  root = root || createRoot(retrieveContainer(props))
  
  loadHubConfig().then(() => {
    root.render(rootComponent())
  })
}

render({})
